import type { RequestOptions } from "ls/api/common/server";
import type { RouteLocationNormalized, RouteLocationResolvedGeneric } from "vue-router";
import type { ChangeAccountInfoModel, JoinAxureData, ResetPasswordModel, SignUpModel } from "./utils";
import type { ListJs, ResetPasswordValidateJs, ResponseObject, SsoResponse, TimeZoneJs } from "@/generated/models";
import type { AxShareHostsConfig } from "@/services/models/config";
import { useAxureCloudConfig } from "ls/state/useAxureCloudConfig";
import { joinURL } from "ufo";
import { computed, readonly, ref, watch } from "vue";
import { authParamName, redirectParamName } from "@/common/axshare/routing";
import { isDesktopLocal, isDevelopment, isElectron } from "@/common/environment";
import { testSameOrigin } from "@/common/lib/browser";
import { Main } from "@/desktop/events";
import { objectToFormData } from "@/services/utils/formData";
import { authToken } from "./authTokenStorage";
import { useServerInstance } from "./useServerInstance";
import { getLoginPayload } from "./utils";

export type ExternalLoginProvider = "Google" | "Microsoft";

export interface ExternalLoginProviderOptions {
  redirect?: string;
  loginHint?: string;
}

export function useAxureCloudAccountService() {
  const config = useAxureCloudConfig();

  const accountServerUrl = ref<string>();
  const accountServer = useServerInstance(accountServerUrl);

  const appServerUrl = ref<string>();
  const appServer = useServerInstance(appServerUrl);

  const currentDomainServer = useServerInstance(() => window.location.origin);

  const clientAppUrl = ref<string>();

  const isSameAsApiHost = computed(() => {
    if (!accountServerUrl.value || !appServerUrl.value) return true;
    return testSameOrigin(accountServerUrl.value, appServerUrl.value);
  });

  const loginBaseUrl = computed(() => {
    if (!isSameAsApiHost.value) {
      if (!accountServerUrl.value) {
        return undefined;
      }
      return joinURL(accountServerUrl.value, "/app/login");
    }

    if (clientAppUrl.value) {
      return joinURL(clientAppUrl.value, "login");
    }

    return joinURL(window.location.origin, "/app/login");
  });

  watch(config, value => {
    if (!value) {
      appServerUrl.value = undefined;
      accountServerUrl.value = undefined;
      return;
    }

    appServerUrl.value = value.AxShareHostSecureUrl;
    accountServerUrl.value = value.AccountServiceSecureUrl;
  }, { immediate: true });

  async function auth(withConfig?: AxShareHostsConfig) {
    if (isElectron) {
      const config: AxShareHostsConfig = withConfig || await window.AxureCloudNative.ipc.invoke(Main.ConfigGet);
      if (config.authToken) {
        authToken.set(config.authToken);
      }
    }
    const authServer = (isDevelopment || isDesktopLocal) ? appServer : currentDomainServer;
    const sso = await authServer.value.post("user/oauth2").json() as SsoResponse;

    if (sso.success && sso.authToken) {
      authToken.set(sso.authToken);
    }

    return sso;
  }

  async function login(email: string, password: string, redirect?: string) {
    if (!config.value) {
      throw new Error("Failed to login. Axure Cloud account service is not configured");
    }

    const model = await getLoginPayload({
      email,
      password,
      config: config.value,
      redirect,
    });

    // ASK: can we use our existing "exec" call to enable support of "redirects"?
    const response = await accountServer.value.post("user/doSignIn", { body: objectToFormData(model) });
    const sso = await response.json() as SsoResponse;

    if (sso.success) {
      if (sso.authToken) {
        authToken.set(sso.authToken);
      }

      if (sso.redirecturl) {
        window.location.href = sso.redirecturl;
        return;
      }
    }

    return sso;
  }

  async function logout() {
    if (isSameAsApiHost.value) {
      await accountServer.value.post("user/logout?isAjax=true");
    } else {
      await Promise.all([
        accountServer.value.post("user/logout?isAjax=true"),
        appServer.value.post("user/logout?isAjax=true"),
      ]);
    }

    authToken.clear();
  }

  async function updateUserProfileName(name: string) {
    const formData = objectToFormData({ name });
    const response = await accountServer.value.post("user/updateUserProfileName", { body: formData });
    return await response.json() as ResponseObject;
  }

  async function uploadUserProfileImg(fileToUpload: File) {
    const formData = objectToFormData({ fileToUpload });
    const response = await accountServer.value.post("user/uploadUserProfileImg", { body: formData });
    return await response.json() as ResponseObject & { imgUrl: string };
  }

  async function deleteUserProfileImg() {
    const response = await accountServer.value.post("user/deleteUserProfileImg");
    return await response.json() as ResponseObject;
  }

  async function changeAccountInfo(model: ChangeAccountInfoModel) {
    const formData = objectToFormData(model);
    const response = await accountServer.value.post("user/changeAccountInfo", { body: formData });
    const data = await response.json() as ResponseObject & { authToken: string };
    if (data.success && data.authToken) {
      await refreshAuth(data.authToken);
    }
    return data;
  }

  async function refreshAuth(token: string) {
    authToken.set(token);
    const formData = objectToFormData({ token });
    if (isSameAsApiHost.value) {
      await accountServer.value.post("user/refreshAuth", { body: formData });
    } else {
      await Promise.all([
        accountServer.value.post("user/refreshAuth", { body: formData }),
        appServer.value.post("user/refreshAuth", { body: formData }),
      ]);
    }
  }

  async function forgotPassword(email: string, target: string, redirect?: string) {
    const response = await accountServer.value.post("user/forgotPassword", {
      body: objectToFormData({ email, target, redirect }),
    });
    return await response.json() as ResponseObject;
  }

  async function resetPasswordValidateToken(token: string) {
    const response = await accountServer.value.post("user/resetPasswordValidateToken", {
      body: objectToFormData({ token }),
    });
    return await response.json() as ResetPasswordValidateJs;
  }

  async function getTimeZones({ signal }: RequestOptions = {}) {
    const response = await accountServer.value.get("user/getTimeZones", { signal });
    return await response.json() as ListJs<TimeZoneJs>;
  }

  async function setUserTimeZone(timeZoneId?: string) {
    const baseUtcOffsetMinutes = -(new Date().getTimezoneOffset());
    const formData = objectToFormData({ timeZone: timeZoneId, baseUtcOffsetMinutes });

    const response = await accountServer.value.post("user/setLocalTimeZone", { body: formData });
    return await response.json() as ResponseObject;
  }

  function getPrototypeLoginPopupUrl(shortcut: string) {
    if (loginBaseUrl.value) {
      const fullLoginUrl = new URL(loginBaseUrl.value);
      if (shortcut) {
        fullLoginUrl.searchParams.append(redirectParamName, joinURL(window.location.origin, "/prototype/auth", shortcut));
      }

      return fullLoginUrl.href;
    }
  }

  function getLoginFullUrl(to?: RouteLocationNormalized | RouteLocationResolvedGeneric) {
    if (!loginBaseUrl.value) return undefined;
    const fullLoginUrl = new URL(loginBaseUrl.value);

    const afterAuthRedirectUrl = getAfterAuthRedirectUrl(to);
    if (!afterAuthRedirectUrl) return undefined;

    fullLoginUrl.searchParams.append(redirectParamName, afterAuthRedirectUrl);
    return fullLoginUrl.href;
  }

  function getSignupUrl() {
    if (!isSameAsApiHost.value) {
      if (!accountServerUrl.value) {
        return undefined;
      }
      return joinURL(accountServerUrl.value, "/app/signup");
    }

    if (appServerUrl.value) {
      return joinURL(appServerUrl.value, "/app/signup");
    }

    return joinURL(window.location.origin, "/app/signup");
  }

  function getAfterAuthRedirectUrl(to?: RouteLocationNormalized | RouteLocationResolvedGeneric) {
    const clientAfterAuthUrl = new URL("/app/login", window.location.origin);
    clientAfterAuthUrl.searchParams.append(authParamName, "true");

    const redirectParam = to?.query[redirectParamName];
    let redirect = Array.isArray(redirectParam) ? redirectParam[0] : redirectParam;
    if (!redirect && to && to.fullPath !== undefined) {
      redirect = to.fullPath.split("#")[0];
    }
    if (redirect) {
      clientAfterAuthUrl.searchParams.append(redirectParamName, redirect);
    }
    if (!appServerUrl.value) {
      return undefined;
    }
    const appServerAuthUrl = new URL(joinURL(appServerUrl.value, "/user/axureAuth"));
    appServerAuthUrl.searchParams.append(redirectParamName, clientAfterAuthUrl.href);
    return appServerAuthUrl.href;
  }

  async function signUp(options: SignUpModel) {
    const response = await accountServer.value.post("user/create", {
      body: objectToFormData(options),
    });
    return await response.json() as SsoResponse;
  }

  async function resetPassword(options: ResetPasswordModel) {
    const response = await accountServer.value.post("user/resetPasswordSpa", {
      body: objectToFormData(options),
    });
    return await response.json() as SsoResponse;
  }

  function getExternalLoginUrl(provider: ExternalLoginProvider, options: ExternalLoginProviderOptions = {}) {
    if (!accountServerUrl.value) {
      return undefined;
    }

    const externalLoginHref = joinURL(accountServerUrl.value, "/user/loginExternal");
    const externalLoginUrl = new URL(externalLoginHref);
    externalLoginUrl.searchParams.append("provider", provider);

    if (options.redirect) {
      externalLoginUrl.searchParams.append("redirect", options.redirect);
    }

    if (options.loginHint) {
      externalLoginUrl.searchParams.append("loginHint", options.loginHint);
    }

    return externalLoginUrl;
  }

  async function visitorLogin(email: string) {
    if (!config.value) {
      throw new Error("Failed to login. Axure Cloud account service is not configured");
    }

    const response = await appServer.value.post("user/visitorLogin", {
      body: objectToFormData({ email }),
    });
    const sso = await response.json() as SsoResponse;

    if (sso.success && sso.authToken) {
      authToken.set(sso.authToken);
    }

    return sso;
  }

  function getJoinAxureUrl(joinAxureData: JoinAxureData, redirect?: string) {
    if (!accountServerUrl.value) {
      throw new Error("Axure Cloud account service is not configured");
    }

    const url = new URL(joinURL(accountServerUrl.value, "/app/join-axure"));

    if (joinAxureData.userEmail) {
      url.searchParams.set("userEmail", joinAxureData.userEmail);
    }

    if (joinAxureData.organizationName) {
      url.searchParams.set("organizationName", joinAxureData.organizationName);
    }

    if (joinAxureData.workspaceName) {
      url.searchParams.set("workspaceName", joinAxureData.workspaceName);
    }

    if (redirect) {
      url.searchParams.set("redirect", redirect);
    }

    return url.toString();
  }

  return {
    auth,
    login,
    visitorLogin,
    logout,
    updateUserProfileName,
    uploadUserProfileImg,
    deleteUserProfileImg,
    changeAccountInfo,
    forgotPassword,
    getTimeZones,
    setUserTimeZone,
    isSameAsApiHost,
    getLoginFullUrl,
    getPrototypeLoginPopupUrl,
    getSignupUrl,
    getExternalLoginUrl,
    getJoinAxureUrl,
    signUp,
    resetPasswordValidateToken,
    resetPassword,
    appServerUrl: readonly(appServerUrl),
    accountServerUrl: readonly(accountServerUrl),
  };
}
